import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Flame, Zap, Trophy, Target, ChevronRight, BookOpen, MessageCircle, Star, GraduationCap, CheckCircle, Award, Sparkles } from "lucide-react";
import { Progress } from "../components/ui/progress";
import ReadinessWidget from "../components/ReadinessWidget";

const Dashboard = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [progress, setProgress] = useState(null);
  const [tier, setTier] = useState(null);
  const [archieRec, setArchieRec] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cats, leaders, prog, tierData, rec] = await Promise.all([
        api.getCategories(),
        api.getLeaderboard(5),
        api.getProgress(),
        api.getUserTier().catch(() => null),
        api.archieRecommendation().catch(() => null),
      ]);
      setCategories(cats);
      setLeaderboard(leaders);
      setProgress(prog);
      setTier(tierData);
      setArchieRec(rec);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const xpToNextLevel = 100;
  const currentLevelXP = (user?.xp || 0) % 100;
  const progressPercent = (currentLevelXP / xpToNextLevel) * 100;

  const categoryColors = {
    sat: "bg-indigo-500",
    act: "bg-lime-500",
    asvab: "bg-orange-500",
    star: "bg-sky-500",
    citizenship: "bg-red-500"
  };

  const categoryIcons = {
    sat: "📚",
    act: "🎓",
    asvab: "🛡️",
    star: "⭐",
    citizenship: "🇺🇸"
  };

  const gradeNames = {
    "6": "6th Grade",
    "7": "7th Grade", 
    "8": "8th Grade",
    "9": "9th Grade",
    "10": "10th Grade",
    "11": "11th Grade",
    "12": "12th Grade",
    "college": "College",
    "adult": "Adult Learner"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-bounce-subtle">
          <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-white">A</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up" data-testid="dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-3xl p-6 lg:p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-black text-2xl lg:text-3xl">
              Welcome back, {user?.name?.split(' ')[0]}! 👋
            </h1>
            <p className="text-indigo-100 mt-2">Ready to crush some practice questions today?</p>
          </div>
          {user?.grade && (
            <div className="hidden sm:flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <GraduationCap className="w-5 h-5" />
              <span className="font-bold">{gradeNames[user.grade] || user.grade}</span>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Daily Streak */}
        <div className="card-pop" data-testid="daily-streak-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Flame className="w-6 h-6 text-orange-500 flame-glow" />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium">Streak</p>
              <p className="font-heading font-black text-2xl text-slate-800">{user?.streak || 0}</p>
            </div>
          </div>
        </div>

        {/* Total XP */}
        <div className="card-pop" data-testid="total-xp-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-indigo-500 gem-glow" />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium">Total XP</p>
              <p className="font-heading font-black text-2xl text-slate-800">{user?.xp || 0}</p>
            </div>
          </div>
        </div>

        {/* Level */}
        <div className="card-pop" data-testid="level-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-lime-100 rounded-xl flex items-center justify-center">
              <Star className="w-6 h-6 text-lime-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium">Level</p>
              <p className="font-heading font-black text-2xl text-slate-800">{user?.level || 1}</p>
            </div>
          </div>
        </div>

        {/* Daily Goal */}
        <div className="card-pop" data-testid="daily-goal-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-sky-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium">Daily Goal</p>
              <p className="font-heading font-black text-2xl text-slate-800">50 XP</p>
            </div>
          </div>
        </div>
      </div>

      {/* Level Progress */}
      <div className="card-pop" data-testid="level-progress">
        <div className="flex items-center justify-between mb-3">
          <span className="font-bold text-slate-700">Level {user?.level || 1}</span>
          <span className="text-sm text-slate-500">{currentLevelXP} / {xpToNextLevel} XP</span>
        </div>
        <div className="progress-chunky">
          <div 
            className="progress-chunky-fill" 
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <p className="text-sm text-slate-400 mt-2">{xpToNextLevel - currentLevelXP} XP to Level {(user?.level || 1) + 1}</p>
      </div>

      {/* Test Readiness Score Widget */}
      <ReadinessWidget category={user?.test} isPremium={tier?.is_premium} />

      {/* Archie Daily Recommendation */}
      {archieRec && (
        <div className="card-pop bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-200" data-testid="archie-daily-rec">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-pink-600 uppercase tracking-wide mb-1">Archie recommends</p>
              <p className="text-slate-800 font-bold mb-2">{archieRec.recommendation}</p>
              {archieRec.suggested_topic && (
                <Link
                  to={`/lessons?category=${archieRec.category}&topic=${encodeURIComponent(archieRec.suggested_topic)}`}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-pink-500 text-white rounded-full text-sm font-bold hover:bg-pink-600"
                  data-testid="archie-rec-cta"
                >
                  Start Lesson <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Exam Readiness Section */}
      {progress?.mastery && progress.mastery.length > 0 && (
        <div className="card-pop" data-testid="exam-readiness">
          <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-500" />
            Exam Readiness
            {progress.overall_exam_ready && (
              <span className="ml-2 px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> ALL EXAMS READY
              </span>
            )}
          </h2>
          <div className="grid gap-3">
            {progress.mastery.filter(m => user?.test === m.category || !user?.test).slice(0, 3).map((mastery) => (
              <div key={mastery.category} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{categoryIcons[mastery.category] || '📖'}</span>
                    <span className="font-bold text-slate-800">{mastery.category_name}</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    mastery.exam_ready 
                      ? 'bg-green-100 text-green-700' 
                      : mastery.mastery_level === 'Advanced'
                      ? 'bg-blue-100 text-blue-700'
                      : mastery.mastery_level === 'Intermediate'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {mastery.exam_ready ? '🏆 EXAM READY' : mastery.mastery_level}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        mastery.exam_ready ? 'bg-green-500' : 'bg-indigo-500'
                      }`}
                      style={{ width: `${(mastery.topics_mastered / mastery.topics_total) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-slate-600">
                    {mastery.topics_mastered}/{mastery.topics_total} topics
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  {mastery.accuracy}% accuracy • {mastery.exam_ready 
                    ? "You're ready to ace this exam!" 
                    : `Master ${mastery.topics_total - mastery.topics_mastered} more topics to be exam ready`
                  }
                </p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3 text-center">
            Master 75% of topics with 80%+ accuracy in each to become Exam Ready
          </p>
        </div>
      )}

      {/* Personalized Recommendations */}
      {progress?.mastery && user?.test && (
        <div className="card-pop" data-testid="recommendations">
          <h2 className="font-heading font-bold text-lg text-white mb-3 flex items-center gap-2">
            <Target className="w-5 h-5 text-[#00D46A]" />
            Recommended for You
          </h2>
          {(() => {
            const userMastery = progress.mastery.find(m => m.category === user.test);
            if (!userMastery) return null;

            // Find weak topics (low accuracy or not mastered)
            const weakTopics = userMastery.topics
              .filter(t => !t.mastered && t.total > 0)
              .sort((a, b) => a.accuracy - b.accuracy)
              .slice(0, 3);

            const unseenTopics = userMastery.topics
              .filter(t => t.total === 0)
              .slice(0, 2);

            return (
              <div className="space-y-2">
                {weakTopics.length > 0 && (
                  <div className="p-3 rounded-lg" style={{ backgroundColor: '#0A1628', border: '1px solid #1E3A5F' }}>
                    <p className="text-sm font-medium mb-2" style={{ color: '#fdba74' }}>Focus Areas (Need Practice)</p>
                    <div className="flex flex-wrap gap-2">
                      {weakTopics.map(topic => (
                        <span key={topic.topic} className="px-3 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: 'rgba(249,115,22,0.18)', color: '#fdba74' }}>
                          {topic.topic} ({topic.accuracy}%)
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {unseenTopics.length > 0 && (
                  <div className="p-3 rounded-lg" style={{ backgroundColor: '#0A1628', border: '1px solid #1E3A5F' }}>
                    <p className="text-sm font-medium mb-2" style={{ color: '#7dd3fc' }}>New Topics to Explore</p>
                    <div className="flex flex-wrap gap-2">
                      {unseenTopics.map(topic => (
                        <span key={topic.topic} className="px-3 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: 'rgba(14,165,233,0.18)', color: '#7dd3fc' }}>
                          {topic.topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {weakTopics.length === 0 && unseenTopics.length === 0 && (
                  <p className="text-sm" style={{ color: '#00FF7F' }}>Great job! Keep practicing to maintain your mastery.</p>
                )}
                <Link
                  to={`/lesson/${user.test}`}
                  className="block mt-3 text-center font-bold text-sm"
                  style={{ color: '#00D46A' }}
                >
                  Start {userMastery.category_name} Practice →
                </Link>
              </div>
            );
          })()}
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Link 
          to="/categories" 
          data-testid="start-practice-btn"
          className="card-pop card-interactive flex items-center gap-4 group"
        >
          <div className="w-14 h-14 bg-lime-500 rounded-2xl flex items-center justify-center shadow-[0_3px_0_0_#65a30d] group-hover:shadow-[0_2px_0_0_#65a30d] group-hover:translate-y-[1px] transition-all">
            <BookOpen className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg text-slate-800">Start Practice</h3>
            <p className="text-slate-500 text-sm">Choose a test and begin learning</p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
        </Link>

        <Link 
          to="/tutor" 
          data-testid="ask-tutor-btn"
          className="card-pop card-interactive flex items-center gap-4 group"
        >
          <div className="w-14 h-14 bg-indigo-500 rounded-2xl flex items-center justify-center shadow-[0_3px_0_0_#4f46e5] group-hover:shadow-[0_2px_0_0_#4f46e5] group-hover:translate-y-[1px] transition-all">
            <MessageCircle className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg text-slate-800">AI Tutor</h3>
            <p className="text-slate-500 text-sm">Get help from Archie</p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      {/* Bottom Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Continue Learning */}
        <div className="card-pop" data-testid="continue-learning">
          <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-500" />
            Continue Learning
          </h2>
          <div className="space-y-3">
            {categories.slice(0, 3).map((cat) => (
              <Link
                key={cat.id}
                to={`/lesson/${cat.id}`}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group"
              >
                <div className={`w-10 h-10 ${categoryColors[cat.id]} rounded-xl flex items-center justify-center text-xl`}>
                  {categoryIcons[cat.id]}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-slate-700">{cat.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${categoryColors[cat.id]} rounded-full`}
                        style={{ width: `${(cat.completed_lessons / cat.total_lessons) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-400">{cat.completed_lessons}/{cat.total_lessons}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
          <Link to="/categories" className="block mt-4 text-center text-indigo-500 font-bold hover:text-indigo-600">
            View All Tests →
          </Link>
        </div>

        {/* Leaderboard Preview */}
        <div className="card-pop" data-testid="leaderboard-preview">
          <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-orange-500" />
            Top Learners
          </h2>
          <div className="space-y-3">
            {leaderboard.map((entry, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  entry.name === user?.name ? "bg-lime-50 border-2 border-lime-200" : ""
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  index === 0 ? "bg-yellow-400 text-yellow-900" :
                  index === 1 ? "bg-slate-300 text-slate-700" :
                  index === 2 ? "bg-orange-300 text-orange-900" :
                  "bg-slate-100 text-slate-500"
                }`}>
                  {entry.rank}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-slate-700">{entry.name}</p>
                  <p className="text-xs text-slate-400">Level {entry.level}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-indigo-500">{entry.xp} XP</p>
                  {entry.streak > 0 && (
                    <p className="text-xs text-orange-500 flex items-center gap-1">
                      <Flame className="w-3 h-3" /> {entry.streak}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Link to="/leaderboard" className="block mt-4 text-center text-indigo-500 font-bold hover:text-indigo-600">
            View Full Leaderboard →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
